import React from 'react';
import { getNodeChildren, calculateRect, getAnchorStyle } from '../utils.js';
import { getBehavior } from './behaviors/registry.js';
import ComponentRenderer from './ComponentRenderer.jsx';
import { resolveNode } from '../utils/responsive.js';

const NodeItem = React.memo(({ controller, isInteractive = true }) => {
  const {
    node: rawNode,
    activeNodes,
    selectedNodeIds,
    prefabs,
    isLiveMode,
    isSpacePressed,
    containerNode,
    onMouseDown,
    onDoubleClick,
    onResizeMouseDown,
    onAxisDragMouseDown,
    rootW,
    rootH,
    isolationStatus,
    contextPath,
    activeBreakpoint
  } = controller;
  // Resolve Node for Responsive Overrides
  const node = React.useMemo(() => resolveNode(rawNode, activeBreakpoint), [rawNode, activeBreakpoint]);

  const isSelected = selectedNodeIds.includes(node.id);

  // Enabled Check - if disabled, hide in edit mode and always hide in live mode
  const isEnabled = node.props?.enabled !== false;
  if (!isEnabled) return null;

  // Visibility Check - only applies to edit mode
  let isVisible = true;
  if (!isLiveMode && node.props?.visibility !== undefined) {
      if (typeof node.props.visibility === 'boolean') {
          isVisible = node.props.visibility;
      } else if (activeBreakpoint && typeof node.props.visibility === 'object') {
          // If visibility object exists, check current breakpoint
          // If key missing, default to true (visible)
          if (node.props.visibility[activeBreakpoint] === false) {
              isVisible = false;
          }
      }
  }

  // If hidden (edit mode only), don't render
  if (!isVisible) return null;

  const children = getNodeChildren(activeNodes, node.id);

  const rawParent = activeNodes.find(n => n.id === node.parentId);
  const parent = rawParent ? resolveNode(rawParent, activeBreakpoint) : null;
  let parentBehaviorDef;
  if (parent && parent.attachedComponents) {
    for (const ac of parent.attachedComponents) {
      const def = getBehavior(ac.type);
      if (def && def.isLayoutProvider) {
        parentBehaviorDef = def;
        break;
      }
    }
  }

  const visualRect = calculateRect(node, rootW, rootH);
  const isLayoutManaged = !!parentBehaviorDef;

  // Check if node has ContentSizeFitter and get its config
  const contentSizeFitterComp = node.attachedComponents?.find(
    comp => comp.type === 'ContentSizeFitter' && comp.props?.enabled !== false
  );
  const contentSizeFitterConfig = contentSizeFitterComp ? contentSizeFitterComp.props : false;

  const style = getAnchorStyle(node, rootW, rootH, isLayoutManaged, contentSizeFitterConfig);

  if (parentBehaviorDef && parentBehaviorDef.getChildStyle && parent) {
    const ac = parent.attachedComponents?.find(c => c.type === parentBehaviorDef.id);
    if (ac) {
      const childStyles = parentBehaviorDef.getChildStyle(ac.props, node.props, node);
      Object.assign(style, childStyles);
    }
  }

  // Determine if we're in isolation mode (drilling into a container)
  const isIsolationMode = isolationStatus === 'target' || isolationStatus === 'ancestor' || isolationStatus === 'descendant';

  // Determine if node is a direct child of the current container (or root)
  // This enforces hierarchical selection: children are not interactive until their parent is entered
  const isDirectChild = containerNode
      ? node.parentId == containerNode.id
      : (node.parentId === undefined || node.parentId === null);

  // Visual & Interaction States based on Isolation
  let opacityClass = 'opacity-100';
  let effectClass = '';
  let pointerEventsStyle = 'auto'; // Default to auto

  if (isolationStatus === 'inactive') {
    opacityClass = 'opacity-30 grayscale';
    pointerEventsStyle = 'none';
  } else if (isolationStatus === 'ancestor') {
    opacityClass = 'opacity-100';
    pointerEventsStyle = 'none'; // Pass through to children
  } else if (isolationStatus === 'target') {
    opacityClass = 'opacity-100';
    pointerEventsStyle = 'auto';
    effectClass = 'shadow-[0_0_0_1000px_rgba(0,0,0,0.1)] z-20'; // Simulate local highlight
  } else if (isolationStatus === 'descendant' || isolationStatus === 'none') {
    opacityClass = 'opacity-100';
    // Only direct children of the active context are interactive
    // OR if the node is explicitly selected (so we can move it even if deep)
    pointerEventsStyle = (isInteractive || isSelected) ? 'auto' : 'none';
  }

  // Override: If locked, disable pointer events (click-through)
  if (node.props?.locked) {
      pointerEventsStyle = 'none';
  }

  const handlers =
    isLiveMode || isolationStatus === 'inactive' || isolationStatus === 'ancestor'
      ? {}
      : {
          onMouseDown: (e) => onMouseDown(e, node.id),
          onDoubleClick: (e) => onDoubleClick(e, node.id)
        };

  // Remove selection outline from NodeItem itself as it's now in GizmoOverlay
  // But keep hover effect
  const selectionClass = isSelected
    ? ''
    : isolationStatus !== 'inactive'
    ? 'hover:outline hover:outline-1 hover:outline-blue-300'
    : '';

  const rendererKey = `${node.id}-${node.props?._variantUpdate || 0}`;

  // Visual indication for hidden items in Design Mode
  const hiddenStyle = (!isVisible && !isLiveMode) ? { opacity: 0.3, filter: 'grayscale(100%)' } : {};

  // Build wrapper props for ComponentRenderer (merged rendering - eliminates extra div)
  // Apply pointer-events as inline style to guarantee precedence
  const wrapperStyle = { ...style, ...hiddenStyle, pointerEvents: isLiveMode ? 'auto' : pointerEventsStyle };
  const wrapperClassName = isLiveMode
    ? ''
    : `group ${opacityClass} ${effectClass} transition-opacity duration-300 ${selectionClass}`;
  const wrapperProps = { id: node.id };

  return (
    <ComponentRenderer
      key={rendererKey}
      node={node}
      prefabs={prefabs}
      isLiveMode={isLiveMode}
      activeBreakpoint={activeBreakpoint}
      allNodes={activeNodes}
      computedSize={visualRect}
      wrapperStyle={wrapperStyle}
      wrapperClassName={wrapperClassName}
      wrapperHandlers={handlers}
      wrapperProps={wrapperProps}
    >
      {children.map(child => {
          // Compute Child Status
          let childStatus = 'none';
          if (isolationStatus === 'none') childStatus = 'none';
          else if (isolationStatus === 'inactive') childStatus = 'inactive';
          else if (isolationStatus === 'target' || isolationStatus === 'descendant')
            childStatus = 'descendant';
          else if (isolationStatus === 'ancestor') {
            if (containerNode && child.id === containerNode.id) childStatus = 'target';
            else if (contextPath.includes(child.id)) childStatus = 'ancestor';
            else childStatus = 'inactive';
          }

          // Calculate interactivity for next level
          // Children are only interactive if their parent is the currently active container (Target)
          // Or if we are in Root mode ('none'), root nodes are interactive (handled by default prop),
          // but THEIR children (this loop) should be non-interactive until entered.
          let childInteractive = false;
          if (isolationStatus === 'target') {
              childInteractive = true;
          }

          const childController = {
            node: child,
            activeNodes,
            selectedNodeIds,
            prefabs,
            isLiveMode,
            isSpacePressed,
            containerNode,
            onMouseDown,
            onDoubleClick,
            onResizeMouseDown,
            onAxisDragMouseDown,
            rootW: isIsolationMode ? visualRect.width : rootW,
            rootH: isIsolationMode ? visualRect.height : rootH,
            isolationStatus: childStatus,
            contextPath,
            activeBreakpoint,
          };

          return (
            <NodeItem
              key={child.id}
              controller={childController}
              isInteractive={childInteractive}
            />
          );
        })}
    </ComponentRenderer>
  );
});

export default NodeItem;
